import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ChatSearchPage.module.css';
import searchIcon from '../../../assets/pic/search.svg';
import roomImg from '../../../assets/pic/chatlist_pic.png';
import resolveMediaUrl from '../../../utils/resolveMediaUrl';
// You might need a close icon or back icon
// import closeIcon from '../../../assets/pic/close.svg'; 
import { searchChatAll, searchChatMessages } from '../../../apis/chatApi';
import useDataStore from '../../../stores/useDataStore';
import pic1 from '../../../assets/pic/pic1.png';
import pic2 from '../../../assets/pic/pic2.png';
import pic3 from '../../../assets/pic/pic3.png';
import pic4 from '../../../assets/pic/pic4.png';
import pic5 from '../../../assets/pic/pic5.png';
import pic6 from '../../../assets/pic/pic6.png';

const getDefaultProfileImage = (userId) => {
    if (!userId) return pic1;
    // userId가 숫자면 모듈러 연산, 문자열이면 해시 연산
    let val = 0;
    if (typeof userId === 'number') {
        val = userId;
    } else {
        const str = String(userId);
        for (let i = 0; i < str.length; i++) {
            val += str.charCodeAt(i);
        }
    }
    const images = [pic1, pic2, pic3, pic4, pic5, pic6];
    return images[val % images.length];
};

const AI_PLAN_PREFIX = '::AI_PLAN::';
const AI_PLAN_PREVIEW = '🗺️ AI 여행 일정이 생성되었습니다';

const normalizeAiPlanText = (text) => {
    if (typeof text !== 'string') return text ?? '';
    return text.startsWith(AI_PLAN_PREFIX) ? AI_PLAN_PREVIEW : text;
};

// Helper to highlight keyword
const HighlightText = React.memo(({ text, keyword }) => {
    if (!keyword || !text) return <span>{text}</span>;
    const parts = text.split(new RegExp(`(${keyword})`, 'gi'));
    return (
        <span>
            {parts.map((part, i) =>
                part.toLowerCase() === keyword.toLowerCase() ?
                    <span key={i} className={styles.highlight}>{part}</span> : part
            )}
        </span>
    );
});

const ChatSearchPage = () => {
    const navigate = useNavigate();
    const { chatRooms } = useDataStore();
    const [keyword, setKeyword] = useState('');
    const [activeTab, setActiveTab] = useState('ALL'); // ALL, ROOMS, MESSAGES
    const [results, setResults] = useState(null); // { rooms: [], messages: [] }
    const [loading, setLoading] = useState(false);

    const getRoomImageUrl = (room) => (
        room?.imageUrl ??
        room?.roomImageUrl ??
        room?.thumbnailUrl ??
        room?.image_url ??
        room?.room_image_url ??
        null
    );

    const getRoomId = (room) => (
        room?.roomId ??
        room?.room_id ??
        room?.id ??
        null
    );

    const getRoomName = (room) => (
        room?.name ??
        room?.roomName ??
        room?.room_name ??
        null
    );

    const summaryById = useCallback(() => {
        const list = Array.isArray(chatRooms) ? chatRooms : [];
        const m = new Map();
        for (const r of list) {
            const id = getRoomId(r);
            if (id !== null && id !== undefined) {
                m.set(String(id), r);
            }
        }
        return m;
    }, [chatRooms]);

    const normalizeRoom = useCallback((room) => {
        const id = getRoomId(room);
        const map = summaryById();
        const summary = id !== null && id !== undefined ? map.get(String(id)) : null;

        const imageUrl = getRoomImageUrl(room) || getRoomImageUrl(summary);
        const name = getRoomName(room) || getRoomName(summary);

        return {
            ...room,
            roomId: room?.roomId ?? id,
            id: room?.id ?? id,
            name: room?.name ?? name,
            imageUrl: room?.imageUrl ?? imageUrl,
        };
    }, [summaryById]);

    // Debounce Logic or Enter Key? User said "Search Screen", usually behaves on typing or Enter.
    // Let's do on Enter for now to be safe with API calls, or debounce if requested.
    // "GET /api/chats/search?keyword=..."

    const fetchResults = useCallback(async (query, tab) => {
        if (!query.trim()) {
            setResults(null);
            return;
        }
        setLoading(true);
        try {
            if (tab === 'ALL') {
                const data = await searchChatAll(query);
                const rooms = Array.isArray(data?.rooms) ? data.rooms.map(normalizeRoom) : [];
                setResults({
                    ...data,
                    rooms,
                });
            } else if (tab === 'MESSAGES') {
                const data = await searchChatMessages(query, 0, 50);
                setResults({ rooms: [], messages: data });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [normalizeRoom]);

    // Live search with small debounce so it feels responsive but doesn't spam.
    useEffect(() => {
        const q = keyword.trim();
        if (!q) {
            setResults(null);
            return;
        }

        const t = setTimeout(() => {
            fetchResults(q, activeTab);
        }, 300);

        return () => clearTimeout(t);
    }, [keyword, activeTab, fetchResults]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            fetchResults(keyword, activeTab);
        }
    };

    const clearSearch = () => {
        setKeyword('');
        setResults(null);
    };

    const handleRoomClick = (roomId) => {
        navigate('/chatroom', { state: { roomId } });
    };

    const formatTime = (ts) => {
        if (!ts) return '';
        const date = new Date(ts);
        return `${date.getMonth() + 1}월 ${date.getDate()}일`;
    };

    const renderRooms = (rooms, isAllTab) => {
        if (!rooms || rooms.length === 0) return null;
        // If ALL tab, show max 3? The API already summarizes for ALL tab?
        // User spec: "Summary (default 3 each)" for ALL tab.
        // If ROOMS tab, API returns list.

        return (
            <div className={styles.sectionCard}>
                <div className={styles.sectionHeaderRow}>
                    <span className={styles.sectionTitle}>채팅방</span>
                </div>
                <div className={styles.listGroup}>
                    {rooms.map((room) => {
                        const id = getRoomId(room);
                        const name = getRoomName(room);
                        const imageUrl = getRoomImageUrl(room);
                        const lastMessageText = normalizeAiPlanText(room.lastMessage);
                        return (
                            <div key={String(id ?? Math.random())} className={styles.roomItem} onClick={() => handleRoomClick(id)}>
                                <img
                                    className={styles.roomImage}
                                    src={resolveMediaUrl(imageUrl) || roomImg}
                                    alt="Room"
                                    loading="lazy"
                                    decoding="async"
                                    onError={(e) => {
                                        console.warn('[search room image load failed]', {
                                            roomId: id,
                                            imageUrl,
                                            resolved: resolveMediaUrl(imageUrl),
                                        });
                                        e.target.src = roomImg;
                                    }}
                                />
                                <div className={styles.roomInfo}>
                                    <div className={styles.roomNameRow}>
                                        <span className={styles.roomName}>
                                            <HighlightText text={name} keyword={keyword} />
                                        </span>
                                        <span className={styles.roomTime}>{formatTime(room.lastMessageAt)}</span>
                                    </div>
                                    <div className={styles.roomLastMsg}>{lastMessageText}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {isAllTab && rooms.length >= 3 && (
                    <button className={styles.moreButton} onClick={() => setActiveTab('ROOMS')}>
                        더보기 &gt;
                    </button>
                )}
            </div>
        );
    };

    const renderMessages = (messages, isAllTab) => {
        if (!messages || messages.length === 0) return null;

        return (
            <div className={styles.sectionCard}>
                <div className={styles.sectionHeaderRow}>
                    <span className={styles.sectionTitle}>메세지</span>
                </div>
                <div className={styles.listGroup}>
                    {messages.map((msg) => (
                        <div key={msg.messageId || Math.random()} className={styles.messageItem} onClick={() => handleRoomClick(msg.roomId)}>
                            {(() => {
                                // 가능한 모든 필드명 체크
                                const rawUrl = msg.profileImageUrl ||
                                    msg.senderProfileImageUrl ||
                                    msg.profile_image_url ||
                                    msg.sender_profile_image_url ||
                                    msg.senderImage ||
                                    msg.avatar;

                                const resolvedUrl = resolveMediaUrl(rawUrl);
                                const senderId = msg.senderUserId || msg.senderId || msg.sender_user_id;

                                // 이미지가 있으면 그거 쓰고, 없으면 기본 이미지
                                const imgSrc = resolvedUrl || getDefaultProfileImage(senderId);

                                return (
                                    <div className={styles.msgAvatarWrapper}>
                                        <img
                                            className={styles.msgAvatar}
                                            src={imgSrc}
                                            alt={msg.senderName}
                                            loading="lazy"
                                            decoding="async"
                                            onError={(e) => {
                                                // 로드 실패 시 fallback (이름 첫글자) 표시
                                                e.currentTarget.style.display = 'none';
                                                e.currentTarget.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <div className={styles.msgAvatarFallback}>
                                            {msg.senderName ? msg.senderName.charAt(0) : '?'}
                                        </div>
                                    </div>
                                );
                            })()}
                            <div className={styles.msgContent}>
                                <div className={styles.msgHeader}>
                                    <div>
                                        <span className={styles.msgSender}>{msg.senderName}</span>
                                        <span className={styles.msgRoomName}>{msg.roomName}</span>
                                    </div>
                                    <span className={styles.msgDate}>{formatTime(msg.timestamp)}</span>
                                </div>
                                <div className={styles.msgText}>
                                    <HighlightText text={normalizeAiPlanText(msg.message)} keyword={keyword} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {isAllTab && messages.length >= 3 && (
                    <button className={styles.moreButton} onClick={() => setActiveTab('MESSAGES')}>
                        더보기 &gt;
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className={styles.page}>
            <div className={styles.searchHeader}>
                <div className={styles.searchBar}>
                    <button
                        type="button"
                        className={styles.searchIconButton}
                        onClick={() => fetchResults(keyword, activeTab)}
                        aria-label="search"
                    >
                        <img src={searchIcon} alt="search" className={styles.searchIcon} />
                    </button>
                    <input
                        className={styles.input}
                        placeholder="검색하기"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoFocus
                    />
                    {keyword && (
                        <button className={styles.clearButton} onClick={clearSearch}>X</button>
                    )}
                </div>

                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'ALL' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('ALL')}
                    >
                        전체
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'ROOMS' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('ROOMS')}
                    >
                        채팅방
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'MESSAGES' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('MESSAGES')}
                    >
                        메세지
                    </button>
                </div>
            </div>

            <div className={styles.contentArea}>
                {!results && !loading && (
                    <div className={styles.recentSearchBox}>
                        <span>최근 검색 저장 기능이 꺼져 있습니다.</span>
                    </div>
                )}

                {loading && <div style={{ textAlign: 'center', marginTop: 20 }}>검색 중...</div>}

                {results && (
                    <>
                        {activeTab === 'ALL' && (
                            <>
                                {renderRooms(results.rooms, true)}
                                {renderMessages(results.messages, true)}
                                {(!results.rooms?.length && !results.messages?.length) &&
                                    <div style={{ textAlign: 'center', color: '#888', marginTop: 50 }}>검색 결과가 없습니다.</div>
                                }
                            </>
                        )}
                        {activeTab === 'ROOMS' && (
                            <>
                                {renderRooms(results.rooms || results, false)}
                                {(!results.rooms?.length && (!Array.isArray(results) || !results.length)) &&
                                    <div style={{ textAlign: 'center', color: '#888', marginTop: 50 }}>검색 결과가 없습니다.</div>
                                }
                            </>
                        )}
                        {activeTab === 'MESSAGES' && (
                            <>
                                {renderMessages(results.messages || results, false)}
                                {(!results.messages?.length && (!Array.isArray(results) || !results.length)) &&
                                    <div style={{ textAlign: 'center', color: '#888', marginTop: 50 }}>검색 결과가 없습니다.</div>
                                }
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ChatSearchPage;
